import React, { useEffect, useState, useRef } from "react";
import "./ProfileModal.css"; // Your CSS + new Template 1 below
import apiService from '../../utils/api';

const ProfileModal = ({ isOpen, onClose, navigate }) => {
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userRole, setUserRole] = useState("");
  const [department, setDepartment] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadStatus, setUploadStatus] = useState('');

  useEffect(() => {
    if (isOpen) {
      const storedName = sessionStorage.getItem("user_name") || "";
      const storedEmail = sessionStorage.getItem("user_emailId") || "";
      const storedRole = sessionStorage.getItem("user_role") || "";
      const storedDept = sessionStorage.getItem("user_department") || "";
      const storedProfileUrl = sessionStorage.getItem("profile_url") || null;

      setUserName(storedName);
      setUserEmail(storedEmail);
      setUserRole(storedRole);
      setDepartment(storedDept);

      const [fName, lName] = storedName.split(" ");
      setFirstName(fName || "");
      setLastName(lName || "");

      if (storedProfileUrl) {
        setProfilePhoto(storedProfileUrl);
      }
    }
  }, [isOpen]);

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadStatus('Uploading...');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('email', userEmail);
      const userId = sessionStorage.getItem('user_id');
      if (userId) formData.append('user_id', userId);

      try {
        const response = await apiService.postData('profileimg/upload-profile-pic', formData);
        if (response.status === 'success' && response.profile_pic_url) {
          const newProfileUrl = response.profile_pic_url;
          setProfilePhoto(newProfileUrl);
          sessionStorage.setItem('profile_url', newProfileUrl);
          setUploadStatus('Profile picture uploaded successfully!');
        } else {
          setUploadStatus('Invalid response from server');
        }
      } catch (error) {
        setUploadStatus(`Failed to upload: ${error.message || 'Unknown error'}`);
      } finally {
        setTimeout(() => setUploadStatus(''), 5000);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirmLogout = async (confirm) => {
    if (confirm) {
      // Clear local storage and session storage first
      localStorage.clear();
      sessionStorage.clear();
      
      // Direct redirect to logout endpoint - let backend handle the redirect
      const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://crclm-backend-dev.bravewave-24971d4f.eastus.azurecontainerapps.io/api';
      window.location.href = `${BASE_URL}/logout`;
    }
    setShowLogoutConfirm(false);
  };

  if (!isOpen) return null;

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-template1" onClick={(e) => e.stopPropagation()}>
        
        {/* Header Section */}
        <div className="header-section">
          <div className="avatar-container">
            {profilePhoto ? (
              <img src={profilePhoto} alt="Avatar" className="avatar" />
            ) : (
              <div className="avatar">{getInitials(userName)}</div>
            )}
            <div className="edit-icon" onClick={() => fileInputRef.current.click()}>
              <svg viewBox="0 0 24 24">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </div>
          </div>
          <div className="user-info">
            <div className="user-name">{userName}</div>
            <div className="user-role">{userRole}</div>
          </div>
        </div>

        {/* Hidden Input */}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handlePhotoUpload}
        />

        {/* Upload Status */}
        {uploadStatus && (
          <div className={`upload-status ${uploadStatus.includes('successfully') ? 'success' : 'error'}`}>
            {uploadStatus}
          </div>
        )}

        {/* Content */}
        <div className="content-section">
          <div className="section-title">Personal Information</div>
          <div className="info-grid">
            <div className="info-item">
              <div className="info-label">First Name</div>
              <div className="info-value">{firstName}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Last Name</div>
              <div className="info-value">{lastName}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Email Address</div>
              <div className="info-value">{userEmail}</div>
            </div>
            
          </div>

          <div className="button-group">
            <button className="btn-close" onClick={onClose}>Close</button>
            <button className="btn-save" onClick={handleLogoutClick}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>

        {/* Logout Confirm (Template 2 - unchanged) */}
        {showLogoutConfirm && (
          <div className="template2 logout-confirm-overlay" onClick={() => setShowLogoutConfirm(false)}>
            <div className="template2 modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="image-section">
                <div className="logout-icon">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                </div>
              </div>
              <div className="content-section">
                <h2>Confirm Logout</h2>
                <p className="quote">
                  "Managing contracts is not just about paperwork—it's about building trust and delivering value."
                </p>
                <div className="button-group">
                  <button className="btn-no" onClick={() => handleConfirmLogout(false)}>
                    Stay Logged In
                  </button>
                  <button className="btn-yes" onClick={() => handleConfirmLogout(true)}>
                    Yes, Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileModal;